import React, { useEffect, useState, useCallback, useRef } from 'react';
import adminApi from '../api/client';

// -- Interfaces --------------------------------------------------------

interface Story {
  id: string;
  person_name: string;
  title_en: string;
  title_si: string;
  full_story_en: string;
  full_story_si: string;
  life_lesson_en: string;
  life_lesson_si: string;
  category: string;
  volume: number;
  mindset_tags: string | null;
  is_verified: boolean | null;
}

interface StoriesResponse {
  total: number;
  page: number;
  limit: number;
  stories: Story[];
}

interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

// -- Toast Container ---------------------------------------------------

const ToastContainer: React.FC<{ toasts: Toast[]; onDismiss: (id: number) => void }> = ({ toasts, onDismiss }) => (
  <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border toast-enter cursor-pointer min-w-[280px] ${toast.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}
        onClick={() => onDismiss(toast.id)}
      >
        <span className="text-lg">{toast.type === 'success' ? '?' : '?'}</span>
        <span className="text-sm font-medium flex-1">{toast.message}</span>
      </div>
    ))}
  </div>
);

// -- Confirm Dialog ----------------------------------------------------

const ConfirmDialog: React.FC<{
  open: boolean; title: string; message: string; onConfirm: () => void; onCancel: () => void; loading: boolean;
}> = ({ open, title, message, onConfirm, onCancel, loading }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-slide-up">
        <h3 className="text-lg font-bold text-gray-800 mb-2">{title}</h3>
        <p className="text-sm text-gray-500 mb-6">{message}</p>
        <div className="flex justify-end gap-3">
          <button onClick={onCancel} disabled={loading} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium transition-colors">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className="px-4 py-2 bg-[#D85A30] hover:bg-[#D85A30]/90 text-white rounded-xl text-sm font-bold transition-colors disabled:opacity-50">
            {loading ? 'Processing...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

// -- Modal -------------------------------------------------------------

const Modal: React.FC<{ isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode }> = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-6 sm:p-8 animate-slide-up max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 bg-gray-50 p-2 rounded-full transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

// -- Category Badge ----------------------------------------------------

const CATEGORY_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  billionaire:    { bg: 'bg-[#BA7517]/15',  text: 'text-[#BA7517]',  label: 'Billionaire' },
  athlete:        { bg: 'bg-[#1D9E75]/15',  text: 'text-[#1D9E75]',  label: 'Athlete' },
  leader:         { bg: 'bg-[#085041]/15',  text: 'text-[#085041]',  label: 'Leader' },
  warrior_women:  { bg: 'bg-[#534AB7]/15',  text: 'text-[#534AB7]',  label: 'Warrior Women' },
  asian_legend:   { bg: 'bg-[#D85A30]/15',  text: 'text-[#D85A30]',  label: 'Asian Legend' },
  visionary:      { bg: 'bg-blue-100',       text: 'text-blue-700',   label: 'Visionary' },
  president:      { bg: 'bg-[#1E3A5F]/15',  text: 'text-[#1E3A5F]',  label: 'President & World Leader' },
  tech_giant:     { bg: 'bg-[#1A1A2E]/15',  text: 'text-[#1A1A2E]',  label: 'Tech Giant & CEO' },
  scientist:      { bg: 'bg-[#0D3B2E]/15',  text: 'text-[#0D3B2E]',  label: 'Scientist & Inventor' },
  dark_lesson:    { bg: 'bg-[#3B0A0A]/15',  text: 'text-[#3B0A0A]',  label: 'Dark Lesson ⚠️' },
  philosopher:    { bg: 'bg-[#2D1B4E]/15',  text: 'text-[#2D1B4E]',  label: 'Philosopher' },
  author:         { bg: 'bg-[#2B1D0E]/15',  text: 'text-[#2B1D0E]',  label: 'Author & Writer' },
};

const CategoryBadge: React.FC<{ category: string }> = ({ category }) => {
  const style = CATEGORY_STYLES[category] ?? { bg: 'bg-gray-100', text: 'text-gray-600', label: category };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border border-current/20 ${style.bg} ${style.text} whitespace-nowrap`}>
      {style.label}
    </span>
  );
};

// -- Main Component ----------------------------------------------------

const CATEGORIES = [
  { value: 'all',           label: 'All Categories' },
  { value: 'billionaire',   label: 'Billionaire' },
  { value: 'athlete',       label: 'Athlete' },
  { value: 'leader',        label: 'Leader' },
  { value: 'warrior_women', label: 'Warrior Women' },
  { value: 'asian_legend',  label: 'Asian Legend' },
  { value: 'visionary',     label: 'Visionary' },
  { value: 'president',     label: 'President & World Leader' },
  { value: 'tech_giant',    label: 'Tech Giant & CEO' },
  { value: 'scientist',     label: 'Scientist & Inventor' },
  { value: 'dark_lesson',   label: 'Dark Lesson ⚠️' },
  { value: 'philosopher',   label: 'Philosopher' },
  { value: 'author',        label: 'Author & Writer' },
];

const emptyForm = {
  person_name: '',
  title_en: '',
  title_si: '',
  full_story_en: '',
  full_story_si: '',
  life_lesson_en: '',
  life_lesson_si: '',
  category: 'billionaire',
  volume: 1,
  mindset_tags: '',
  is_verified: false,
};

const Stories: React.FC = () => {
  // -- Data State --
  const [stories, setStories] = useState<Story[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(false);
  const perPage = 20;

  // -- Filter State --
  const [volumeFilter, setVolumeFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const search = searchQuery.trim();
  const listScope = JSON.stringify([page, volumeFilter, categoryFilter, search]);
  const activeListScope = useRef(listScope);
  activeListScope.current = listScope;
  const listRequest = useRef(0);
  const invalidateListRequests = useCallback(() => { listRequest.current++; }, []);
  const [volumes, setVolumes] = useState<{volume: number; story_count: number}[]>([]);

  // -- Toast State --
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastIdRef = useRef(0);

  // -- Modal State --
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });

  // -- Delete Confirm State --
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string }>({ open: false, id: '' });
  const [isDeleting, setIsDeleting] = useState(false);

  // -- Toggle Verified --
  const [togglingId, setTogglingId] = useState<string | null>(null);

  // -- Toast Helper --
  const addToast = useCallback((message: string, type: 'success' | 'error') => {
    const id = ++toastIdRef.current;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => { setToasts((prev) => prev.filter((t) => t.id !== id)); }, 4000);
  }, []);

  // -- Fetch Volumes --
  const fetchVolumes = useCallback(async () => {
    try {
      const res = await adminApi.get<{ volumes: {volume: number; story_count: number}[] }>('/admin/stories/volumes');
      setVolumes(res.data.volumes ?? []);
    } catch {
      // Silently ignore
    }
  }, []);

  // -- Fetch Stories --
  const fetchStories = useCallback(async () => {
    // An old mutation's refresh callback must not fetch a former search/page.
    if (activeListScope.current !== listScope) return;
    const request = ++listRequest.current;
    const isCurrent = () => request === listRequest.current && activeListScope.current === listScope;
    try {
      setLoading(true);
      setListError(false);
      const params: Record<string, any> = { page, limit: perPage };
      if (volumeFilter !== 'all') params.volume = Number(volumeFilter);
      if (categoryFilter !== 'all') params.category = categoryFilter;
      if (search) params.q = search;
      const res = await adminApi.get<StoriesResponse>('/admin/stories', { params });
      if (!isCurrent()) return;
      setStories(res.data.stories || []);
      setTotal(res.data.total || 0);
    } catch {
      if (!isCurrent()) return;
      setStories([]); setTotal(0); setListError(true);
      addToast('Failed to load stories.', 'error');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [page, volumeFilter, categoryFilter, search, listScope, addToast]);

  useEffect(() => { fetchVolumes(); }, [fetchVolumes]);
  useEffect(() => {
    setLoading(true); setListError(false);
    const timer = setTimeout(fetchStories, 300);
    return () => { clearTimeout(timer); invalidateListRequests(); };
  }, [fetchStories, invalidateListRequests]);

  // -- Modal Helpers --
  const openAddModal = () => {
    setEditingId(null);
    setFormData({ ...emptyForm });
    setIsModalOpen(true);
  };

  const openEditModal = (s: Story) => {
    setEditingId(s.id);
    setFormData({
      person_name: s.person_name || '',
      title_en: s.title_en || '',
      title_si: s.title_si || '',
      full_story_en: s.full_story_en || '',
      full_story_si: s.full_story_si || '',
      life_lesson_en: s.life_lesson_en || '',
      life_lesson_si: s.life_lesson_si || '',
      category: s.category || 'billionaire',
      volume: s.volume || 1,
      mindset_tags: s.mindset_tags || '',
      is_verified: s.is_verified ?? false,
    });
    setIsModalOpen(true);
  };

  // -- Form Submit --
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        person_name: formData.person_name,
        title_en: formData.title_en,
        title_si: formData.title_si,
        full_story_en: formData.full_story_en,
        full_story_si: formData.full_story_si,
        life_lesson_en: formData.life_lesson_en,
        life_lesson_si: formData.life_lesson_si,
        category: formData.category,
        volume: Number(formData.volume),
        mindset_tags: formData.mindset_tags,
        is_verified: formData.is_verified,
      };
      if (editingId) {
        await adminApi.put(`/admin/stories/${editingId}`, payload);
        addToast('Story updated successfully.', 'success');
      } else {
        await adminApi.post('/admin/stories', payload);
        addToast('Story added successfully.', 'success');
      }
      setIsModalOpen(false);
      fetchStories();
    } catch (err: any) {
      addToast(err.response?.data?.detail || 'Failed to save story.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // -- Delete --
  const handleDelete = async () => {
    if (!deleteConfirm.id) return;
    try {
      setIsDeleting(true);
      await adminApi.delete(`/admin/stories/${deleteConfirm.id}`);
      addToast('Story deleted.', 'success');
      setDeleteConfirm({ open: false, id: '' });
      fetchStories();
    } catch (err: any) {
      addToast(err.response?.data?.detail || 'Failed to delete story.', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  // -- Toggle Verified --
  const handleToggleVerified = async (story: Story) => {
    setTogglingId(story.id);
    try {
      await adminApi.patch(`/admin/stories/${story.id}/toggle-verified`);
      addToast(`Story ${story.is_verified ? 'unpublished' : 'verified'}.`, 'success');
      fetchStories();
    } catch (err: any) {
      addToast(err.response?.data?.detail || 'Failed to toggle verified status.', 'error');
    } finally {
      setTogglingId(null);
    }
  };

  const totalPages = Math.ceil(total / perPage);

  // -- Render ------------------------------------------------------------

  return (
    <div className="space-y-8 animate-fade-in">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
            ?? Path of Greatness
            <span className="px-3 py-1 bg-[#1D9E75]/10 text-[#1D9E75] rounded-full text-sm font-semibold border border-[#1D9E75]/20">
              {loading ? 'Loading…' : listError ? 'Count unavailable' : `${total} Stories`}
            </span>
          </h1>
          <p className="text-sm text-gray-400 mt-1">Manage life stories of inspiring people</p>
        </div>
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#1D9E75] hover:bg-[#15815f] text-white rounded-xl font-bold transition-all shadow-sm shadow-[#1D9E75]/20 active:scale-95 sm:ml-auto"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          Add Story
        </button>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={volumeFilter}
          onChange={(e) => { setVolumeFilter(e.target.value); setPage(1); }}
          className="bg-white border border-gray-200 text-sm font-medium rounded-xl px-4 py-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] shadow-sm"
        >
          <option value="all">All Volumes</option>
          {volumes.map((v) => (
            <option key={v.volume} value={v.volume}>Vol {v.volume} ({v.story_count})</option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
          className="bg-white border border-gray-200 text-sm font-medium rounded-xl px-4 py-2.5 text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] shadow-sm"
        >
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            maxLength={150}
            placeholder="Search by person name..."
            className="w-full pl-9 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] shadow-sm"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/80 border-b border-gray-100">
              <tr>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Person</th>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Title (EN)</th>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Volume</th>
                <th className="px-5 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Tags</th>
                <th className="px-5 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Verified</th>
                <th className="px-5 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className="px-5 py-4"><div className="h-4 bg-gray-100 rounded w-full" /></td>
                    ))}
                  </tr>
                ))
              ) : listError ? (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center">
                    <div role="alert" className="text-red-600">Failed to load stories.</div>
                    <button type="button" onClick={fetchStories} className="mt-3 px-4 py-2 border rounded-xl">Retry</button>
                  </td>
                </tr>
              ) : stories.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-gray-400 font-medium text-base">
                    No stories found.
                  </td>
                </tr>
              ) : (
                stories.map((story) => {
                  const tags = String(story.mindset_tags ?? '').split(',').map((t) => t.trim()).filter(Boolean);
                  return (
                    <tr key={story.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-4">
                        <p className="text-sm font-bold text-gray-800 whitespace-nowrap">{story.person_name}</p>
                      </td>
                      <td className="px-5 py-4 max-w-[220px]">
                        <p className="text-sm text-gray-600 truncate">
                          {story.title_en.length > 60 ? story.title_en.substring(0, 60) + '…' : story.title_en}
                        </p>
                      </td>
                      <td className="px-5 py-4">
                        <CategoryBadge category={story.category} />
                      </td>
                      <td className="px-5 py-4">
                        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-[#085041]/10 text-[#085041] border border-[#085041]/20 whitespace-nowrap">
                          Vol {story.volume}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-wrap gap-1">
                          {tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-600 border border-gray-200">
                              {tag}
                            </span>
                          ))}
                          {tags.length > 2 && (
                            <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-400">
                              +{tags.length - 2}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <button
                          onClick={() => handleToggleVerified(story)}
                          disabled={togglingId === story.id}
                          title={story.is_verified ? 'Click to unpublish' : 'Click to verify'}
                          className="text-xl transition-all active:scale-90 disabled:opacity-50 hover:scale-110"
                        >
                          {story.is_verified === true ? '✅' : story.is_verified === false ? '⭕' : '?'}
                        </button>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(story)}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#BA7517]/10 text-[#BA7517] hover:bg-[#BA7517]/20 border border-[#BA7517]/20 transition-all active:scale-95"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => setDeleteConfirm({ open: true, id: story.id })}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#D85A30]/10 text-[#D85A30] hover:bg-[#D85A30]/20 border border-[#D85A30]/20 transition-all active:scale-95"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && !listError && total > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/30">
            <span className="text-sm text-gray-500">
              Showing <span className="font-semibold text-gray-700">{(page - 1) * perPage + 1}</span>–
              <span className="font-semibold text-gray-700">{Math.min(page * perPage, total)}</span> of <span className="font-semibold text-gray-700">{total}</span>
            </span>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-4 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors">Prev</button>
              <span className="text-sm px-3"><span className="font-bold text-[#085041]">{page}</span> / <span className="text-gray-500">{totalPages}</span></span>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-4 py-2 rounded-xl text-sm font-medium bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Add / Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => !isSubmitting && setIsModalOpen(false)} title={editingId ? 'Edit Story' : 'Add New Story'}>
        <form onSubmit={handleFormSubmit} className="space-y-5">

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Person Name <span className="text-[#D85A30]">*</span></label>
            <input
              required
              type="text"
              value={formData.person_name}
              onChange={(e) => setFormData((p) => ({ ...p, person_name: e.target.value }))}
              placeholder="e.g. Elon Musk"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] transition-all"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Title (English) <span className="text-[#D85A30]">*</span></label>
              <input
                required
                type="text"
                value={formData.title_en}
                onChange={(e) => setFormData((p) => ({ ...p, title_en: e.target.value }))}
                placeholder="Story title in English"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:border-[#1D9E75] transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Title (Sinhala)</label>
              <input
                type="text"
                value={formData.title_si}
                onChange={(e) => setFormData((p) => ({ ...p, title_si: e.target.value }))}
                placeholder="????? ???????"
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:border-[#1D9E75] transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData((p) => ({ ...p, category: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-white focus:outline-none focus:border-[#1D9E75]"
              >
                <option value="billionaire">Billionaire</option>
                <option value="athlete">Athlete</option>
                <option value="leader">Leader</option>
                <option value="warrior_women">Warrior Women</option>
                <option value="asian_legend">Asian Legend</option>
                <option value="visionary">Visionary</option>
                <option value="president">President &amp; World Leader</option>
                <option value="tech_giant">Tech Giant &amp; CEO</option>
                <option value="scientist">Scientist &amp; Inventor</option>
                <option value="dark_lesson">Dark Lesson ⚠️</option>
                <option value="philosopher">Philosopher</option>
                <option value="author">Author &amp; Writer</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Volume</label>
              <input
                type="number"
                min={1}
                value={formData.volume}
                onChange={(e) => setFormData((p) => ({ ...p, volume: Number(e.target.value) }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:border-[#1D9E75] transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Mindset Tags</label>
            <input
              type="text"
              value={formData.mindset_tags}
              onChange={(e) => setFormData((p) => ({ ...p, mindset_tags: e.target.value }))}
              placeholder="Resilience,Discipline,Grit"
              className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:border-[#1D9E75] transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Full Story (English) <span className="text-[#D85A30]">*</span></label>
            <textarea
              required
              rows={6}
              value={formData.full_story_en}
              onChange={(e) => setFormData((p) => ({ ...p, full_story_en: e.target.value }))}
              placeholder="Write the full story in English..."
              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] transition-all resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Full Story (Sinhala)</label>
            <textarea
              rows={4}
              value={formData.full_story_si}
              onChange={(e) => setFormData((p) => ({ ...p, full_story_si: e.target.value }))}
              placeholder="????? ???????? ????..."
              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-800 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1D9E75]/20 focus:border-[#1D9E75] transition-all resize-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Life Lesson (English)</label>
              <textarea
                rows={2}
                value={formData.life_lesson_en}
                onChange={(e) => setFormData((p) => ({ ...p, life_lesson_en: e.target.value }))}
                placeholder="Key takeaway in English..."
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:border-[#1D9E75] transition-all resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Life Lesson (Sinhala)</label>
              <textarea
                rows={2}
                value={formData.life_lesson_si}
                onChange={(e) => setFormData((p) => ({ ...p, life_lesson_si: e.target.value }))}
                placeholder="????? ????..."
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:border-[#1D9E75] transition-all resize-none"
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-1">
            <input
              id="is_verified_checkbox"
              type="checkbox"
              checked={formData.is_verified}
              onChange={(e) => setFormData((p) => ({ ...p, is_verified: e.target.checked }))}
              className="w-4 h-4 accent-[#1D9E75] cursor-pointer"
            />
            <label htmlFor="is_verified_checkbox" className="text-sm font-bold text-gray-700 cursor-pointer select-none">
              Mark as Verified / Published
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-6 mt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl text-gray-600 bg-gray-100 hover:bg-gray-200 font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl text-white bg-[#085041] hover:bg-[#063d32] font-bold transition-all shadow-md shadow-[#085041]/20 active:scale-95 disabled:opacity-50 flex items-center justify-center min-w-[130px]"
            >
              {isSubmitting ? (
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : editingId ? 'Save Changes' : 'Add Story'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Confirm Dialog & Toasts */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete Story"
        message="Are you sure you want to delete this story? This action cannot be undone."
        loading={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm({ open: false, id: '' })}
      />
      <ToastContainer toasts={toasts} onDismiss={(id) => setToasts((p) => p.filter((t) => t.id !== id))} />
    </div>
  );
};

export default Stories;

